import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Download, FileImage, FileText, Film } from 'lucide-react';

function FadeIn({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay, ease: [0.23, 1, 0.32, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

const downloads = [
  {
    title: 'Plaquette Photographies',
    desc: 'Reportages photos terrestres et subaquatiques — Cession de droits photographiques',
    icon: FileImage,
    preview: 'https://customer-assets.emergentagent.com/job_template-astud/artifacts/c71xsvqz_1%20-%20Photographies.jpg',
    url: 'https://customer-assets.emergentagent.com/job_template-astud/artifacts/c71xsvqz_1%20-%20Photographies.jpg',
    type: 'JPG',
  },
  {
    title: 'Plaquette Audiovisuelle',
    desc: 'Prestations audiovisuelles — Aquatique Productions',
    icon: Film,
    preview: 'https://customer-assets.emergentagent.com/job_template-astud/artifacts/xn1pfyz8_2%20-%20Audiovisuelle.jpg',
    url: 'https://customer-assets.emergentagent.com/job_template-astud/artifacts/xn1pfyz8_2%20-%20Audiovisuelle.jpg',
    type: 'JPG',
  },
  {
    title: 'Plaquette Offre - Recto',
    desc: 'Presentation generale des prestations Image & Riviere',
    icon: FileImage,
    preview: 'https://customer-assets.emergentagent.com/job_template-astud/artifacts/x9f63j9b_Plaquette%20offre%201.jpg',
    url: 'https://customer-assets.emergentagent.com/job_template-astud/artifacts/x9f63j9b_Plaquette%20offre%201.jpg',
    type: 'JPG',
  },
  {
    title: 'Plaquette Offre - Verso',
    desc: 'Details des prestations — Expositions, galerie, conference',
    icon: FileImage,
    preview: 'https://customer-assets.emergentagent.com/job_template-astud/artifacts/fl96h13g_Plaquette%20offre%202.jpg',
    url: 'https://customer-assets.emergentagent.com/job_template-astud/artifacts/fl96h13g_Plaquette%20offre%202.jpg',
    type: 'JPG',
  },
  {
    title: 'Portfolio complet',
    desc: 'Portfolio PDF — Ensemble des prestations et realisations',
    icon: FileText,
    preview: null,
    url: 'https://customer-assets.emergentagent.com/job_template-astud/artifacts/advidvvf_5%20-%20Portfolio.pdf',
    type: 'PDF',
  },
];

export default function DownloadsPage() {
  return (
    <main data-testid="downloads-page">
      {/* Hero */}
      <section className="relative h-[40vh] overflow-hidden">
        <img src="https://customer-assets.emergentagent.com/job_template-astud/artifacts/prq4obov__DSC4113.jpg" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1D15] via-[#0B1D15]/60 to-[#0B1D15]/30" />
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-12 z-10 px-6">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-playfair text-5xl md:text-6xl text-white text-center tracking-tight mb-3">
            Centre de telechargement
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="font-lato text-lg text-[#8CA1B0] text-center">
            Retrouvez nos plaquettes et documents a telecharger
          </motion.p>
        </div>
      </section>

      {/* Downloads grid */}
      <section className="py-20 bg-gradient-to-b from-[#0B1D15] to-[#0F2318] relative overflow-hidden">
        <div className="caustics-overlay" />
        <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {downloads.map((dl, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div className="glass-light rounded-2xl overflow-hidden h-full flex flex-col" data-testid={`download-card-${i}`}>
                  {/* Preview */}
                  {dl.preview ? (
                    <div className="h-52 overflow-hidden">
                      <img src={dl.preview} alt={dl.title} className="w-full h-full object-cover object-top" loading="lazy" />
                    </div>
                  ) : (
                    <div className="h-52 bg-[#0D2B3E] flex items-center justify-center">
                      <dl.icon size={48} className="text-[#2B7BA8]/40" />
                    </div>
                  )}

                  {/* Info */}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-montserrat text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#2B7BA8]/20 text-[#7DD4F5]">
                        {dl.type}
                      </span>
                    </div>
                    <h3 className="font-montserrat text-base font-semibold text-white mb-2">{dl.title}</h3>
                    <p className="font-lato text-sm text-[#8CA1B0] leading-relaxed mb-5 flex-1">{dl.desc}</p>
                    <a
                      href={dl.url}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`download-btn-${i}`}
                      className="inline-flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-[#2B7BA8] text-white font-montserrat text-sm hover:bg-[#5AACE0] transition-colors"
                    >
                      <Download size={16} />
                      Telecharger
                    </a>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
